import React from 'react'

import { Box, Button, Icon, Text } from '@factorialco/deprecated-design-system'
import {
  EditSpendingPolicyRuleGroupFragment,
  UpdateSpendingPolicyRuleGroupDocument,
} from 'generated/resources/graphql'

import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EditRuleFormProps, EditRulesForm } from '../EditRuleForm'
import { OptionItem } from '../ruleFormHelpers'

interface Props {
  ruleGroup: EditSpendingPolicyRuleGroupFragment
  onSuccess?: () => void
  timeFrameOptions: OptionItem[]
  onDelete: (ruleGroup: EditSpendingPolicyRuleGroupFragment) => void
}

export const EditRuleGroupForm: React.FC<Props> = ({
  ruleGroup,
  onSuccess,
  onDelete,
  timeFrameOptions,
}) => {
  const [updateGroup] = useMutation(UpdateSpendingPolicyRuleGroupDocument, {
    extractorFn: (data) => data.financeMutations.updateFinanceSpendingPolicySpendingPolicyRuleGroup,
  })

  const mapRuleToFormValue = (identifier: string) => {
    return (
      ruleGroup.spendingPolicyRules
        .find((formRule) => formRule.identifier === identifier)
        ?.value.at(0) ?? ''
    )
  }

  const getRuleIdByIdentifier = (identifier: string) => {
    const ruleId = ruleGroup.spendingPolicyRules.find(
      (formRule) => formRule.identifier === identifier
    )?.id

    if (!ruleId) {
      throw new Error(`Rule with identifier ${identifier} not found`)
    }
    return ruleId.toString()
  }

  const {
    isSubmitting,
    values: formValues,
    setErrors,
    setFieldValue,
    handleSubmit,
    resetForm,
    dirty,
    errors,
  } = useForm<EditRuleFormProps>({
    enableReinitialize: true,
    validateOnChange: false,

    defaultValues: {
      parentRuleGroupId: ruleGroup.parentRuleGroup?.id.toString(),
      companyId: ruleGroup.companyId.toString(),
      description: ruleGroup.description,
      categoryId: ruleGroup.parentRuleGroup
        ? mapRuleToFormValue('subcategory')
        : mapRuleToFormValue('category'),
      amount: mapRuleToFormValue('amount'),
      timeFrame: mapRuleToFormValue('time_frame'),
      currency: mapRuleToFormValue('currency'),
    },
    validate: (data) => {
      const validationErrors: FormErrors<EditRuleFormProps> = {}

      if (!data.timeFrame) {
        validationErrors.timeFrame = i18n.t('common.validations.empty')
      }
      if (!Number(data.amount)) {
        validationErrors.amount = i18n.t('common.validations.empty')
      }

      return validationErrors
    },
    onSubmit: async (submitValues) => {
      let handled = null

      const result = await updateGroup({
        variables: {
          id: ruleGroup.id.toString(),
          companyId: ruleGroup.companyId.toString(),
          description: submitValues.description,
          spendingPolicyRules: [
            {
              id: getRuleIdByIdentifier(ruleGroup.parentRuleGroup ? 'subcategory' : 'category'),
              value: [submitValues.categoryId],
            },
            {
              id: getRuleIdByIdentifier('amount'),
              value: [submitValues.amount],
            },
            {
              id: getRuleIdByIdentifier('time_frame'),
              value: [submitValues.timeFrame],
            },
            {
              id: getRuleIdByIdentifier('currency'),
              value: [submitValues.currency],
            },
          ],
        },
      })
      handled = handleResult(result, (submitErrors) => {
        if (submitErrors) {
          setErrors(submitErrors)
          app.setFlash({
            type: 'wrong',
            message: i18n.t('spending_policies.details.form.update_error'),
          })
        }
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('spending_policies.details.form.update_success'),
        })

        onSuccess?.()
      }
      return handled
    },
  })

  const findParentRule = (identifier: string) => {
    return ruleGroup.parentRuleGroup?.spendingPolicyRules.find(
      (rule) => rule.identifier === identifier
    )
  }
  const findRule = (identifier: string) => {
    return ruleGroup.spendingPolicyRules.find((rule) => rule.identifier === identifier)
  }

  const updateFormValues = (identifier: keyof EditRuleFormProps, value: string) => {
    setFieldValue(identifier, value)
  }
  const selectedCategory = ruleGroup.parentRuleGroup
    ? findRule('subcategory')?.formatedValue
    : findRule('category')?.formatedValue

  const parentCategory = findParentRule('category')?.formatedValue
  return (
    <Box gap="s24" paddingBottom="s24">
      <Box flexDirection="row" justifyContent="spaceBetween">
        <Text size="300" weight="medium" color="textPrimary">
          <Box flexDirection="row" alignItems="center" gap="s4">
            {parentCategory && (
              <>
                <Text.Normal size="300" color="textPrimary" weight="medium">
                  {parentCategory}
                </Text.Normal>
                <Icon.Tiny color="grey800" icon="chevron-right" />
              </>
            )}
            <Text.Normal size="300" color="textPrimary" weight="medium">
              {selectedCategory}
            </Text.Normal>
          </Box>
        </Text>

        <Button
          onClick={() => onDelete(ruleGroup)}
          title={i18n.t('spending_policies.details.delete_rule.cta')}
          type="secondary"
          iconName="delete"
        ></Button>
      </Box>

      <EditRulesForm
        errors={errors}
        timeFrameOptions={timeFrameOptions}
        updateFormValues={updateFormValues}
        formValues={formValues}
      />

      {dirty && (
        <Box flexDirection="row" justifyContent="flexEnd" gap="s8">
          <Button type="secondary" onClick={() => resetForm()}>
            {i18n.t('common.cancel')}
          </Button>
          <Button loading={isSubmitting} onClick={() => handleSubmit()}>
            {i18n.t('common.submit')}
          </Button>
        </Box>
      )}
    </Box>
  )
}
